import { IconPencil } from "@tabler/icons-react"
import { BREAK_STYLE, CELL_STYLE } from "../lib/cellStyles"
import { CELL_STATE_LABEL, type CellState, type EditorKind, HOST_CYCLE, SITE_CYCLE } from "../lib/editorModel"
import { OVERRIDE_KIND_LABEL, OVERRIDE_LEGEND_HELP, type OverrideKindKey } from "../lib/overrideOverlay"
import { OverrideKindIcon } from "./OverrideKindIcon"

/**
 * The key: the states a cell of this kind of grid can take, in click order, then the break.
 *
 * With `overrideKinds` (the dated week's overlay, AB#803) it goes on to the override markers: one
 * entry per kind in the week, then the help text saying only CMS's own overrides are marked
 * (decision ams-cml). Without it — the weekly default, or the flag off — it is the key it always was.
 * With `pendingLabel` (AB#806) it also names the window of the override being made.
 */
export function AvailabilityLegend({
  kind,
  overrideKinds,
  pendingLabel,
}: {
  kind: EditorKind
  overrideKinds?: readonly OverrideKindKey[]
  /** The kind of the override being made (AB#806): adds the pending window's entry. */
  pendingLabel?: string
}) {
  const states: readonly CellState[] = kind === "site" ? SITE_CYCLE : HOST_CYCLE

  const key = (
    <div className="flex flex-wrap items-center gap-4 text-xs" aria-label="Legend">
      {states.map(state => (
        <span key={state} className="flex items-center gap-1.5">
          <span className="inline-block h-3.5 w-5 rounded-sm" style={CELL_STYLE[state]} />
          {CELL_STATE_LABEL[state]}
        </span>
      ))}
      <span className="flex items-center gap-1.5">
        <span className="inline-block h-3.5 w-5 rounded-sm" style={BREAK_STYLE} />
        Break (locked)
      </span>
      {overrideKinds?.map(overrideKind => (
        <span key={overrideKind} className="flex items-center gap-1.5" data-legend-override={overrideKind}>
          <span aria-hidden className="inline-flex h-3.5 w-5 items-center justify-center rounded-sm outline-dashed outline-1 -outline-offset-1 outline-foreground/60">
            <OverrideKindIcon kind={overrideKind} />
          </span>
          {OVERRIDE_KIND_LABEL[overrideKind]}
        </span>
      ))}
      {pendingLabel !== undefined && (
        <span className="flex items-center gap-1.5" data-legend-pending="">
          <span aria-hidden className="inline-flex h-3.5 w-5 items-center justify-center rounded-sm ring-2 ring-inset ring-primary">
            <IconPencil className="size-3" />
          </span>
          Pending: {pendingLabel}
        </span>
      )}
    </div>
  )

  if (!overrideKinds) return key

  return (
    <div className="flex flex-col gap-1.5">
      {key}
      <p className="text-xs text-muted-foreground">{OVERRIDE_LEGEND_HELP}</p>
    </div>
  )
}
